import React, { useEffect, useMemo, useState } from 'react';
import { FiClock, FiEdit2 } from 'react-icons/fi';
import StepHistory from './StepHistory';

const EMPTY_PROCESS_HISTORY = Object.freeze({
  steps: [],
  stepResponsibles: {},
  stepNames: null,
});

const formatDateTime = (value) => {
  if (!value) return '-';
  try {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleString('pt-BR');
  } catch {
    return '-';
  }
};

const tabClassName = (active) =>
  `inline-flex min-w-0 flex-1 items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold transition sm:flex-none ${
    active
      ? 'bg-white text-blue-700 shadow-sm ring-1 ring-slate-200'
      : 'text-slate-600 hover:bg-white/70 hover:text-slate-900'
  }`;

function AfterCompletionHistory({ editHistory }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-slate-900">
            Histórico após a conclusão
          </h3>
          <p className="text-xs text-slate-500">
            Alterações realizadas na Central de Cadastros depois que o processo foi concluído.
          </p>
        </div>
        <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
          {editHistory.length} registro{editHistory.length === 1 ? '' : 's'}
        </span>
      </div>

      <div className="max-h-80 space-y-3 overflow-y-auto pr-1">
        {editHistory.length === 0 ? (
          <p className="rounded-xl bg-slate-50 p-3 text-sm text-slate-600">
            Nenhuma alteração foi realizada após a conclusão do processo.
          </p>
        ) : (
          editHistory.map((entry, entryIndex) => {
            const entryChanges = Array.isArray(entry?.changes)
              ? entry.changes
              : [];
            const entryKey = entry?.id || `${entry?.timestamp || 'registro'}-${entryIndex}`;

            return (
              <div
                key={entryKey}
                className="rounded-xl border border-slate-200 bg-slate-50/70 p-4 text-xs text-slate-700 shadow-sm"
              >
                <div className="flex flex-wrap items-center justify-between gap-2 text-slate-500">
                  <div className="flex flex-col">
                    <span className="font-medium text-slate-900">
                      {formatDateTime(entry?.timestamp)}
                    </span>
                    <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                      por {entry?.userName || entry?.userEmail || 'Usuário não identificado'}
                    </span>
                  </div>
                  <span className="inline-flex items-center rounded-full bg-white px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-600 ring-1 ring-slate-200">
                    {entryChanges.length} alteração{entryChanges.length === 1 ? '' : 'es'}
                  </span>
                </div>
                <ul className="mt-3 space-y-2">
                  {entryChanges.map((change, changeIndex) => (
                    <li
                      key={`${entryKey}-${change?.fieldId || 'campo'}-${changeIndex}`}
                      className="rounded-lg bg-white p-3 ring-1 ring-slate-200"
                    >
                      <div className="flex flex-wrap items-center gap-x-2">
                        <span className="font-medium text-slate-900">
                          {change?.label || change?.fieldId || 'Campo'}
                        </span>
                        <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                          {change?.scope === 'metadata' ? 'Campo extra' : 'Formulário'}
                          {change?.fieldId ? ` • ${change.fieldId}` : ''}
                        </span>
                      </div>
                      <div className="mt-1 text-slate-600">
                        {change?.from || '—'} → {change?.to || '—'}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

export default function RegistrationHistoryTabs({
  instanceId,
  processHistory,
  editHistory,
}) {
  const [activeTab, setActiveTab] = useState('during');
  const normalizedProcessHistory =
    processHistory && typeof processHistory === 'object'
      ? processHistory
      : EMPTY_PROCESS_HISTORY;
  const normalizedEditHistory = Array.isArray(editHistory) ? editHistory : [];
  const duringCount = Array.isArray(normalizedProcessHistory.steps)
    ? normalizedProcessHistory.steps.length
    : 0;
  const tabIdPrefix = useMemo(
    () => `registration-history-${instanceId || 'selected'}`,
    [instanceId],
  );

  useEffect(() => {
    setActiveTab('during');
  }, [instanceId]);

  return (
    <div className="space-y-4">
      <div
        role="tablist"
        aria-label="Período do histórico"
        className="flex w-full rounded-xl border border-slate-200 bg-slate-100 p-1 sm:w-fit"
      >
        <button
          id={`${tabIdPrefix}-during-tab`}
          type="button"
          role="tab"
          aria-selected={activeTab === 'during'}
          aria-controls={`${tabIdPrefix}-during-panel`}
          onClick={() => setActiveTab('during')}
          className={tabClassName(activeTab === 'during')}
        >
          <FiClock className="h-4 w-4 shrink-0" aria-hidden="true" />
          <span className="truncate">Durante a solicitação</span>
          <span className="rounded-full bg-blue-50 px-2 py-0.5 text-[10px] text-blue-700">
            {duringCount}
          </span>
        </button>
        <button
          id={`${tabIdPrefix}-after-tab`}
          type="button"
          role="tab"
          aria-selected={activeTab === 'after'}
          aria-controls={`${tabIdPrefix}-after-panel`}
          onClick={() => setActiveTab('after')}
          className={tabClassName(activeTab === 'after')}
        >
          <FiEdit2 className="h-4 w-4 shrink-0" aria-hidden="true" />
          <span className="truncate">Após a conclusão</span>
          <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] text-emerald-700">
            {normalizedEditHistory.length}
          </span>
        </button>
      </div>

      {activeTab === 'during' ? (
        <div
          id={`${tabIdPrefix}-during-panel`}
          role="tabpanel"
          aria-labelledby={`${tabIdPrefix}-during-tab`}
        >
          <StepHistory
            instanceId={instanceId}
            historyData={normalizedProcessHistory}
            title="Histórico durante a solicitação"
            description="Tudo o que aconteceu enquanto o processo estava em andamento: responsáveis, tarefas, horários e valores informados."
            emptyMessage="Nenhum evento foi registrado durante a solicitação."
          />
        </div>
      ) : (
        <div
          id={`${tabIdPrefix}-after-panel`}
          role="tabpanel"
          aria-labelledby={`${tabIdPrefix}-after-tab`}
        >
          <AfterCompletionHistory editHistory={normalizedEditHistory} />
        </div>
      )}
    </div>
  );
}
